import { atom, selector, selectorFamily, waitForAll } from 'recoil'
import { keyFactory } from 'src/utils/recoil'
import Fuse from 'fuse.js'
import { UserList, UserListItem } from 'src/common-services'
import * as userListsAtoms from 'src/atoms/user-lists'
import { userListsClient } from 'src/clients'

const key = keyFactory('user-lists-layer')

export const active = atom<boolean>({
  key: key('active'),
  default: false,
})

export const filter = atom<string>({
  key: key('filter'),
  default: '',
})

const searchableUserLists = selector<Fuse<UserList>>({
  key: key('searchable-user-lists'),
  get: async ({ get }) => {
    const lists = get(userListsAtoms.userLists) ?? []
    const selectedIds = get(selectedUserListIds)
    const finalList = lists.filter((l) => !selectedIds.includes(l.id))
    return new Fuse(finalList, { keys: ['name'], threshold: 0.2 })
  },
})

export const filteredUserLists = selector<UserList[]>({
  key: key('filtered-user-lists'),
  get: async ({ get }) => {
    const _filter = get(filter)
    if (!_filter) {
      const lists = get(userListsAtoms.userLists) ?? []
      const selectedIds = get(selectedUserListIds)
      const finalList = lists.filter((l) => !selectedIds.includes(l.id))
      return finalList
    }

    const allLists = get(searchableUserLists)
    return allLists
      .search(_filter)
      .slice(0, 10)
      .map((r) => r.item)
  },
})

export const selectedUserListIds = atom<string[]>({
  key: key('selected-user-list-ids'),
  default: [],
})

export const selectedUserLisItems = selectorFamily<UserListItem[], string>({
  key: key('selected-user-list-items'),
  get:
    (id: string) =>
    async ({ get }) => {
      const lists = get(selectedUserLists)
      const list = lists.find((l) => l.id === id)
      if (!list) {
        return []
      }

      const items = get(userLisItems(list.id))
      return getValidItems(items)
    },
})

export const selectedUserLisAllItems = selector<UserListItem[]>({
  key: key('selected-user-list-all-items'),
  get: async ({ get }) => {
    const lists = get(selectedUserLists)
    if (!lists.length) {
      return []
    }

    const items = get(waitForAll(lists.map((l) => userLisItems(l.id))))
    const allItems = items.flat()
    return getValidItems(allItems)
  },
})

const userLisItems = selectorFamily<UserListItem[], string>({
  key: key('user-list-items'),
  get: (id: string) => async () => {
    const items = await userListsClient.getListItemsByIdDetailed(id)
    return items
  },
})

export const selectedUserLists = selector<UserList[]>({
  key: key('selected-user-lists'),
  get: ({ get }) => {
    const ids = get(selectedUserListIds)
    const lists = get(userListsAtoms.userLists) ?? []
    const selectedLists = lists.filter((l) => ids.includes(l.id))
    return selectedLists
  },
})

export const userListToEditId = atom<string | undefined>({
  key: key('user-list-to-edit-id'),
  default: undefined,
})

export const userListToEdit = selector<UserList | undefined>({
  key: key('user-list-to-edit'),
  get: ({ get }) => {
    const id = get(userListToEditId)
    const lists = get(selectedUserLists)
    return lists.find((l) => l.id === id)
  },
})

export const showLocationsPins = atom<Record<string, boolean>>({
  key: key('show-locations-pins'),
  default: {},
})

export const showRadiusRings = atom<Record<string, boolean>>({
  key: key('show-radius-rings'),
  default: {},
})

export const pinsColor = atom<Record<string, string>>({
  key: key('pins-color'),
  default: {},
})

export const radiusRings = atom<Record<string, [number, number, number]>>({
  key: key('radius-rings'),
  default: {},
})

export const radiusRingsEnabled = atom<Record<string, [boolean, boolean, boolean]>>({
  key: key('radius-rings-enabled'),
  default: {},
})

export const radiusRingsFillColors = atom<Record<string, [string, string, string]>>({
  key: key('radius-rings-fill-colors'),
  default: {},
})

const getValidItems = (items: UserListItem[]) => {
  return items.filter(
    (item) =>
      (item.type === 'place' && item.data.googlePlace?.geometry) ||
      (item.type === 'location' && item.data.location?.latLng?.coordinates)
  )
}
